
import React, { useState } from "react";
import { 
  ModernSidebar, 
  ModernSidebarBody, 
  ModernSidebarLink 
} from "@/components/ui/sidebar/modern-sidebar";
import { Home, Users, History, LogOut, RefreshCw } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { Avatar, AvatarImage } from "@/components/ui/avatar";

import { queryClient } from "@/App";
import { useToast } from "@/components/ui/use-toast";
import { BugReportDialog } from "@/components/BugReportDialog";

export function ModernParentSidebar() {
  const [open, setOpen] = useState(false);
  const { user, signOut } = useAuth();
  const location = useLocation();
  const { toast } = useToast();
  const isLightTheme = false;

  // Refresh handler
  const handleRefresh = () => {
    queryClient.invalidateQueries({
      predicate: (query) => true,
    });
    toast({
      title: "Data refreshed",
      description: "All data has been refreshed from the server.",
    });
  };

  return (
    <ModernSidebar open={open} setOpen={setOpen}>
      <ModernSidebarBody className="flex flex-col h-full">
        {/* Logo - only show on desktop, mobile has it in the top bar */}
        <div className="hidden md:block">
          <Logo open={open} isLightTheme={isLightTheme} />
        </div>
        
        {/* Mobile: Show logo at top of menu */}
        <div className="md:hidden mb-8 mt-4">
          <Logo open={true} isLightTheme={isLightTheme} />
        </div>
        
        {/* Navigation */}
        <div className="flex flex-col gap-2">
          <ModernSidebarLink
            to="/dashboard"
            icon={<Home />}
            label="Dashboard"
            active={location.pathname === "/dashboard"}
          />
          
          <ModernSidebarLink
            to="/children"
            icon={<Users />}
            label="Children"
            active={location.pathname.startsWith("/children")}
          />
          
          <ModernSidebarLink
            to="/activities"
            icon={<History />}
            label="Activity Log"
            active={location.pathname.startsWith("/activities")}
          />
        </div>
        
        {/* Bottom section */}
        <div className="mt-auto">
          {/* Refresh Data */}
          <button 
            className={cn(
              "flex items-center p-3 transition-all duration-300 hover:bg-accent/50 w-full",
              open ? "justify-start gap-3" : "justify-center"
            )}
            onClick={handleRefresh}
          >
            <RefreshCw className="w-5 h-5" />
            {open && <span className="text-sm">Refresh Data</span>}
          </button>
          
          {/* Bug Report */}
          <div className={cn(
            "flex items-center p-3 transition-all duration-300",
            open ? "justify-start" : "justify-center"
          )}>
            <BugReportDialog />
          </div>
          
          {/* User Profile */}
          <div className={cn(
            "flex items-center p-3 mb-3 transition-all duration-300",
            open ? "justify-start" : "justify-center"
          )}>
            <Avatar className="h-8 w-8 rounded-full">
              <AvatarImage src={user?.user_metadata?.avatar_url || ""} alt={user?.user_metadata?.full_name || "User"} />
            </Avatar>
            
            {open && (
              <div className="ml-3 flex flex-col overflow-hidden">
                <span className="text-sm font-medium truncate max-w-[150px]">{user?.user_metadata?.full_name || "Parent"}</span>
                <span className="text-xs text-muted-foreground truncate max-w-[150px]">{user?.email || "parent@sleepbetter.com"}</span>
              </div>
            )}
          </div>
          
          {/* Sign Out */}
          <button
            onClick={signOut}
            className={cn(
              "flex items-center p-3 text-red-500 hover:bg-red-500/10 transition-all duration-300 w-full mb-4",
              open ? "justify-start gap-3" : "justify-center"
            )}
          >
            <LogOut className="w-5 h-5" />
            {open && <span className="text-sm">Sign Out</span>}
          </button>
        </div>
      </ModernSidebarBody>
    </ModernSidebar>
  );
}

// Logo component
function Logo({ open, isLightTheme }: { open: boolean, isLightTheme: boolean }) {
  return (
    <div className={cn(
      "flex items-center px-3 py-4 transition-all duration-300",
      open ? "justify-start" : "justify-center"
    )}>
      <Link to="/dashboard" className="flex items-center">
        <motion.div
          initial={{ scale: 1 }}
          whileHover={{ scale: 1.05 }}
          className="flex items-center"
        >
          <img 
            src="/logo.png"
            alt="SleepNest Logo"
            className="h-8 w-8"
          />
          
          {open && (
            <motion.div
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: "auto" }}
              transition={{ duration: 0.2 }}
              className="ml-3 font-bold text-xl truncate"
            >
              SleepNest
            </motion.div>
          )}
        </motion.div>
      </Link>
    </div>
  );
}
